/**
 * Duplicate Cleanup Panel for Admin
 * หน้าจัดการทำความสะอาดรูปซ้ำสำหรับแอดมิน
 */

import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { cleanupAllDuplicates, cleanupDuplicatesForUser } from '../../utils/duplicateCleanupUtils';

const DuplicateCleanupPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [cleanupResults, setCleanupResults] = useState(null);

  // ดึงข้อมูลผู้ใช้ทั้งหมด
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersSnapshot = await getDocs(collection(db, 'girls'));
      const usersList = [];
      
      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        usersList.push({
          id: doc.id,
          nickname: userData.nickname || 'ไม่ระบุชื่อ',
          type: userData.type || 'normal',
          location: userData.location || 'ไม่ระบุสถานที่',
          imagesCount: (userData.images || []).length,
          pendingCount: (userData.pendingImages || []).length,
          rejectedCount: (userData.rejectedImages || []).length
        });
      });
      
      setUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  // ทำความสะอาดรูปซ้ำสำหรับผู้ใช้คนเดียว
  const cleanupUserDuplicates = async (userId) => {
    try {
      setCleaning(true);
      const result = await cleanupDuplicatesForUser(userId);
      
      setCleanupResults({
        type: 'user',
        userId,
        cleanedCount: result.cleanedCount,
        duplicates: result.duplicates
      });
      
      // รีเฟรชข้อมูลผู้ใช้
      await fetchUsers();
      
      alert(`ทำความสะอาดรูปซ้ำเรียบร้อยแล้ว ลบ ${result.cleanedCount} รูป`);
    } catch (error) {
      console.error('Error cleaning user duplicates:', error);
      alert('เกิดข้อผิดพลาดในการทำความสะอาด');
    } finally {
      setCleaning(false);
    }
  };

  // ทำความสะอาดรูปซ้ำสำหรับผู้ใช้ทั้งหมด
  const cleanupAllDuplicates = async () => {
    try {
      setCleaning(true);
      const result = await cleanupAllDuplicates();
      
      setCleanupResults({
        type: 'all',
        totalCleaned: result.totalCleaned,
        userResults: result.userResults
      });
      
      // รีเฟรชข้อมูลผู้ใช้
      await fetchUsers();
      
      alert(`ทำความสะอาดรูปซ้ำเรียบร้อยแล้ว ลบ ${result.totalCleaned} รูปจากผู้ใช้ ${result.userResults.length} คน`);
    } catch (error) {
      console.error('Error cleaning all duplicates:', error);
      alert('เกิดข้อผิดพลาดในการทำความสะอาด');
    } finally {
      setCleaning(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">ทำความสะอาดรูปซ้ำ</h2>
        <div className="flex gap-2">
          <button
            onClick={fetchUsers}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            🔄 รีเฟรช
          </button>
          <button
            onClick={cleanupAllDuplicates}
            disabled={cleaning}
            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:bg-gray-400"
          >
            {cleaning ? 'กำลังทำความสะอาด...' : '🧹 ทำความสะอาดทั้งหมด'}
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-800 mb-2">สรุปข้อมูล</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{users.length}</div>
            <div className="text-gray-600">ผู้ใช้ทั้งหมด</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {users.reduce((sum, user) => sum + (user.imagesCount || 0), 0)}
            </div>
            <div className="text-gray-600">รูปที่อนุมัติแล้ว</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {users.reduce((sum, user) => sum + (user.pendingCount || 0), 0)}
            </div>
            <div className="text-gray-600">รูปรออนุมัติ</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {users.reduce((sum, user) => sum + (user.rejectedCount || 0), 0)}
            </div>
            <div className="text-gray-600">รูปที่ปฏิเสธ</div>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">รายการผู้ใช้</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ผู้ใช้
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  สถานที่
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  รูปภาพ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  การดำเนินการ
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {user.nickname}
                      </div>
                      <div className="text-sm text-gray-500">
                        {user.type} • {user.id.substring(0, 8)}...
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.location}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-4 text-sm">
                      <div className="text-green-600">
                        ✅ {user.imagesCount || 0}
                      </div>
                      <div className="text-yellow-600">
                        ⏳ {user.pendingCount || 0}
                      </div>
                      <div className="text-red-600">
                        ❌ {user.rejectedCount || 0}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => cleanupUserDuplicates(user.id)}
                      disabled={cleaning}
                      className="text-red-600 hover:text-red-900 disabled:text-gray-400"
                    >
                      🧹 ทำความสะอาด
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cleanup Results */}
      {cleanupResults && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-green-800 mb-2">ผลการทำความสะอาด</h3>
          {cleanupResults.type === 'all' ? (
            <div>
              <p className="text-green-700">
                ทำความสะอาดรูปซ้ำเรียบร้อยแล้ว ลบ {cleanupResults.totalCleaned} รูป
                จากผู้ใช้ {cleanupResults.userResults.length} คน
              </p>
              {cleanupResults.userResults.length > 0 && (
                <div className="mt-3">
                  <h4 className="font-medium text-green-800">รายละเอียด:</h4>
                  <ul className="list-disc list-inside text-sm text-green-700">
                    {cleanupResults.userResults.map((result, index) => (
                      <li key={index}>
                        ผู้ใช้ {result.userId.substring(0, 8)}... ลบ {result.cleanedCount} รูป
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div>
              <p className="text-green-700">
                ทำความสะอาดรูปซ้ำเรียบร้อยแล้ว ลบ {cleanupResults.cleanedCount} รูป
                จากผู้ใช้ {cleanupResults.userId.substring(0, 8)}...
              </p>
              {cleanupResults.duplicates.length > 0 && (
                <div className="mt-3">
                  <h4 className="font-medium text-green-800">รูปที่ลบ:</h4>
                  <ul className="list-disc list-inside text-sm text-green-700">
                    {cleanupResults.duplicates.map((dup, index) => (
                      <li key={index}>
                        {dup.fileName} ({dup.status}) - {dup.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DuplicateCleanupPanel;
